// Die agentischen Zwischenschritte kommen als Roh-HTML im gestreamten Markdown:
// `<div class="progress">…</div>`, oft mehrere hintereinander, getrennt nur durch
// Leerzeilen — teils auch innerhalb einer Liste. Damit aufeinanderfolgende
// Schritte als **eine** Gruppe (ein Card-Rahmen) gerendert werden können, wird
// der Nachrichtentext **vor** dem Markdown-Rendern segmentiert: Schrittgruppen
// hier heraus, alles dazwischen bleibt Markdown und geht weiter durch
// react-markdown.
//
// Der Text eines Schritts ist HTML-escaped (ChatExchange.escapeProgressHtml),
// wird hier also entschärft *und* zurückübersetzt, weil er als Text und nicht
// als HTML gerendert wird.

export type AgentSegment =
    | { kind: 'markdown'; text: string }
    | { kind: 'steps'; steps: string[] };

/** Öffnendes Tag eines Schrittblocks — `class` exakt `progress`, nicht `progressive`. */
const PROGRESS_OPEN = /<div\b[^>]*\sclass\s*=\s*(?:"progress"|'progress'|progress(?=[\s>]))[^>]*>/i;
const PROGRESS_CLOSE = /<\/div\s*>/i;
const TAG = /<\/?[^>]*>/g;

/**
 * Eine Zeile, die nur aus einem Listen-/Zitatmarker besteht. Steht ein
 * Schrittblock in einer Liste, bleibt genau das zwischen zwei Blöcken übrig —
 * es ist kein Inhalt und darf die Gruppe nicht auftrennen. Bewusst **ein**
 * Marker: `---` ist eine Trennlinie und damit echter Inhalt.
 */
const MARKER_ONLY_LINE = /^[ \t]*(?:[-*+•]|\d+[.)]|>)[ \t]*$/gm;

const ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
};

const decodeEntities = (value: string): string =>
    value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
        const lower = body.toLowerCase();
        if (lower.startsWith('#')) {
            const code = lower.startsWith('#x')
                ? parseInt(lower.slice(2), 16)
                : parseInt(lower.slice(1), 10);
            return Number.isFinite(code) && code >= 0 && code <= 0x10ffff
                ? String.fromCodePoint(code)
                : whole;
        }
        return ENTITIES[lower] ?? whole;
    });

/** Trägt der Abschnitt außer Auszeichnung und Listenmarkern nichts? */
const isFiller = (raw: string): boolean =>
    raw.replace(TAG, ' ').replace(MARKER_ONLY_LINE, '').trim() === '';

/** Der sichtbare Text eines Schritts: ohne Tags, Entities aufgelöst, einzeilig. */
const toStepText = (inner: string): string =>
    decodeEntities(inner.replace(TAG, ' ')).replace(/\s+/g, ' ').trim();

/**
 * Zerlegt den Nachrichtentext in Schrittgruppen und Markdown-Abschnitte, in der
 * Reihenfolge des Originals. Ein noch nicht geschlossener Block (der Stream
 * läuft) zählt als letzter Schritt.
 */
export function splitAgentStepSegments(content: string): AgentSegment[] {
    const segments: AgentSegment[] = [];
    let steps: string[] = [];

    const flushSteps = () => {
        if (steps.length > 0) {
            segments.push({ kind: 'steps', steps });
            steps = [];
        }
    };
    const emitMarkdown = (raw: string) => {
        if (isFiller(raw)) return;
        flushSteps();
        segments.push({ kind: 'markdown', text: raw.trim() });
    };

    let rest = content || '';
    for (;;) {
        const open = PROGRESS_OPEN.exec(rest);
        if (!open) {
            emitMarkdown(rest);
            break;
        }

        emitMarkdown(rest.slice(0, open.index));

        const afterOpen = rest.slice(open.index + open[0].length);
        const close = PROGRESS_CLOSE.exec(afterOpen);
        const text = toStepText(close ? afterOpen.slice(0, close.index) : afterOpen);
        if (text) steps.push(text);
        if (!close) break;
        rest = afterOpen.slice(close.index + close[0].length);
    }

    flushSteps();
    return segments;
}
